import { format } from 'date-fns'
import prettyBytes from 'pretty-bytes'

const getColor = (index: number): string => {
  const colors = ['#6C5DD3', '#A3A1FB', '#5DDC75', '#F99074', '#FFC463', '#FF6B6B', '#FFA36B', '#55D4C3', '#59b5da']
  return colors[index % colors.length]
}

export const TOP_5_COLORS = [
  '#EC7A74',
  '#F3B96C',
  '#A5D9CB',
  '#5EB5B3',
  '#4F8AA4',
  '#316075'
]

export const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
]

type Graph = {
  name: string
  value: number
  color: string
  percent: number
}

type Monthly = {
  name: string
  value: number
}

export type Distribution = {
  offerDistribution: Graph[]
  siteDistribution: Graph[]
  monthlyPrice: Monthly[]
  monthlyUsage: Monthly[]
}

interface DateParams {
  createdAtStart: string | undefined
  createdAtEnd: string | undefined
}

const formatDate = (date: Date): string => {
  return format(date, 'dd.MM.yyyy')
}

export const formatDateByLocale = (date: Date | string, locale: string): string => {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date))
}

export const serializeDateRange = ({start, end}) => {
  const newParams: DateParams = {
    createdAtStart: undefined,
    createdAtEnd: undefined
  }

  if (start) {
    newParams.createdAtStart = formatDate(start)
  }

  if (end) {
    const adjustedEnd = new Date(end)
    adjustedEnd.setHours(23, 59, 59, 999)
    newParams.createdAtEnd = formatDate(adjustedEnd)
  }
  return newParams
}

export const formatNumber = (number: number): string => {
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(number);
}

export const formatBytesWithUnit = (bytes: number, locale: string, fractionDigits = 2) => {
  const formatted = prettyBytes(bytes * 1_000_000, {
    locale,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
    .replace('B', 'o')
    .replace(/([KMGTP])B/g, '$1o')
  const parts = formatted.split(' ')
  
  if (parts.length === 2) {
    return {
      value: parts[0],
      unit: parts[1]
    }
  }
  
  return {
    value: formatted,
    unit: ''
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderTotalLabel = (chartData: any[], locale: string) => (props: any) => {
  const { x, y, width, index } = props
  const entry = chartData[index]
  
  if (!entry || entry.total === 0) return null
  
  const { value, unit } = formatBytesWithUnit(entry.total, locale, 0)

  const cx = x + width / 2
  const cy = y - 8
  const fontSize = width < 20 ? '8px' : '10px'

  return (
    <text
      key={`label-${index}`}
      x={cx}
      y={cy}
      fill="#7c7c7cff"
      textAnchor="start"
      fontSize={fontSize}
      fontWeight="500"
      transform={`rotate(-70 ${cx} ${cy})`}
    >
      {value} {unit}
    </text>
  )
}

export const getCivilityLabel = (civility: string | null | undefined, t): string => {
  if (!civility || civility.trim() === '') return '-'
  
  const civilityMap = {
    'mr': t('statistics.customers.civility.mr'),
    'mrs': t('statistics.customers.civility.mrs'),
    'ms': t('statistics.customers.civility.ms'),
    'miss': t('statistics.customers.civility.ms'),
    'lady': t('statistics.customers.civility.lady'),
    'mx': t('statistics.customers.civility.mx'),
    'dame': t('statistics.customers.civility.dame'),
    'sir': t('statistics.customers.civility.sir'),
    'doctor': t('statistics.customers.civility.doctor'),
    'dr': t('statistics.customers.civility.doctor'),
    'professor': t('statistics.customers.civility.professor'),
    'prof': t('statistics.customers.civility.professor'),
    'cllr': t('statistics.customers.civility.cllr'),
    'lord': t('statistics.customers.civility.lord'),
    'reverend': t('statistics.customers.civility.reverend'),
  }
  
  return civilityMap[civility.toLowerCase()] || civility
}

export const getVisitReasonLabel = (visitReason: string | null | undefined, t): string => {
  if (!visitReason || visitReason.trim() === '') return '-'

  const normalizedVisitReason = visitReason.trim().toLowerCase()
  const visitReasonMap = {
    business: t('statistics.customers.visit_reason.business'),
    leisure: t('statistics.customers.visit_reason.leisure'),
  }

  return visitReasonMap[normalizedVisitReason] || visitReason
}

export const convertPricesToKpis = ({ distribution, t }) => {
  return [
    {
      label: t('statistics.total'),
      number: formatNumber(distribution?.global_price || 0),
      textIcon: '€'
    },
    {
      label: t('statistics.average'),
      number: formatNumber(distribution?.global_average_price || 0),
      textIcon: '€'
    }
  ]
}

export const convertDistributionToGraph = (data): Distribution => {
  if (!data) return {
    offerDistribution: [],
    siteDistribution: [],
    monthlyPrice: [],
    monthlyUsage: []
  }

  const { monthly_price, monthly_usage, offers, sites } = data

  const offerDistribution = offers ? offers.map((item, index) => ({
    name: item.name,
    count: item.count,
    value: item.count,
    color: getColor(index),
    percent: ((item.count / offers.reduce((sum, o) => sum + o.count, 0)) * 100).toFixed(2) + '%'
  })) : []

  const siteDistribution = sites ? sites.map((item, index) => ({
    name: item.name,
    value: item.count,
    color: getColor(index),
    percent: ((item.count / sites.reduce((sum, s) => sum + s.count, 0)) * 100).toFixed(2) + '%'
  })) : []

  const monthlyPrice = monthly_price ? monthly_price.map(item => ({
    name: format(new Date(item.start_date), 'MMM').toUpperCase(),
    value: item.count
  })) : []

  const monthlyUsage = monthly_usage ? monthly_usage.map(item => ({
    name: format(new Date(item.start_date), 'MMM').toUpperCase(),
    value: item.count
  })) : []

  return {
    offerDistribution,
    siteDistribution,
    monthlyPrice,
    monthlyUsage
  }
}
